import type {
  AiMessage,
  Conversation,
  ConversationTurnStatus,
} from '../services/ai.service';
import { nativeResponseViews } from './native-response-view';
import { parseMessageContent, stripPageContext } from './attachment-markers';

export type NovaWorkStatus =
  | 'waiting'
  | 'working'
  | 'ready'
  | 'attention'
  | 'cancelled';
export interface NovaWorkItem {
  id: string;
  conversationId: string;
  name: string;
  status: NovaWorkStatus;
  detail: string;
  updatedAt: number;
  unread: boolean;
  reportPath?: string;
  kind?: 'task' | 'report';
  steps?: string[];
}
export interface NovaActivityIdentity {
  userId: string;
  organizationId: string;
}

export function ownsNovaConversation(
  chat: Conversation,
  identity: NovaActivityIdentity
): boolean {
  return (
    !!chat?.id &&
    !chat.deletedAt &&
    chat.attributes?.userId === identity.userId &&
    chat.attributes?.organizationId === identity.organizationId &&
    (chat.attributes?.managedExecution === true ||
      chat.attributes?.novaPhase1 === true)
  );
}

/** Saved, validated report facts determine the label; prose never implies success. */
export function workItemFromUpdate(
  chat: Conversation,
  message: AiMessage,
  unread: boolean
): NovaWorkItem {
  const view = nativeResponseViews(message.views).find(
    (view) => 'report' in view
  );
  const report = view && 'report' in view ? view.report : undefined;
  const ready =
    !!report &&
    /^(scanned|completed|complete|ready|finished)$/i.test(report.status);
  const partial = !!report && /partial/i.test(report.status);
  return {
    id: message.id,
    conversationId: chat.id,
    name: report?.name || chat.name || 'Task update',
    kind: report ? 'report' : 'task',
    status: ready || partial ? 'ready' : 'attention',
    detail: partial
      ? 'Results are available. Some coverage may be incomplete.'
      : ready
      ? 'Your report is ready. Let’s take a look together.'
      : 'There’s an update on this task. Open the conversation for details.',
    updatedAt: Date.parse(message.createdAt) || Date.now(),
    unread,
    reportPath: report?.path,
  };
}

export function novaTaskName(
  request: string | undefined,
  fallback: string
): string {
  const text = request
    ? parseMessageContent(stripPageContext(request))
        .text.replace(/\s+/g, ' ')
        .trim()
    : '';
  const title =
    text.match(/^(.{20,100}?[.!?])(?:\s|$)/)?.[1] ||
    text ||
    fallback ||
    'Working on your request';
  return title.length > 100 ? title.slice(0, 97).trimEnd() + '…' : title;
}

/** A task finishes only when its durable turn says so, never from answer wording. */
export function workItemFromTurn(
  turn: ConversationTurnStatus,
  name: string,
  previous?: NovaWorkItem
): NovaWorkItem {
  const status: NovaWorkStatus =
    turn.status === 'completed'
      ? 'ready'
      : turn.status === 'cancelled'
      ? 'cancelled'
      : turn.status === 'failed'
      ? 'attention'
      : ['preparing', 'queued'].includes(turn.status)
      ? 'waiting'
      : 'working';
  const detail =
    status === 'ready'
      ? 'Your result is ready. Pick up where we left off.'
      : status === 'cancelled'
      ? 'This task was cancelled.'
      : status === 'attention'
      ? turn.errorCode === 'budget_exceeded'
        ? 'This task reached its usage limit. Open the conversation for next steps.'
        : 'I couldn’t finish this task. Let’s check the next step together.'
      : turn.cancellationRequested
      ? 'Stopping this task…'
      : turn.progress || 'Thinking through your request';
  const steps = previous?.steps || [];
  return {
    id: `turn:${turn.turnId}`,
    conversationId: turn.conversationId,
    name,
    kind: 'task',
    status,
    detail,
    updatedAt: Date.now(),
    unread: previous?.unread || false,
    steps:
      ['working', 'waiting'].includes(status) && steps.at(-1) !== detail
        ? [...steps.slice(-3), detail]
        : steps,
  };
}

export function mergeWorkItems(
  items: NovaWorkItem[],
  next: NovaWorkItem
): NovaWorkItem[] {
  // Completion replaces the matching live receipt rather than leaving a stale running row.
  return [
    next,
    ...items.filter(
      (item) =>
        item.id !== next.id &&
        !(
          item.conversationId === next.conversationId &&
          next.kind !== 'task' &&
          item.kind !== 'task' &&
          item.id.startsWith('status:') &&
          next.id.startsWith('background:')
        ) &&
        !(
          item.conversationId === next.conversationId &&
          next.kind !== 'task' &&
          item.kind !== 'task' &&
          item.reportPath === next.reportPath &&
          ['waiting', 'working'].includes(item.status)
        )
    ),
  ]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 100);
}
